import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  TextField,
  TextFieldProps,
  Box,
  Stack,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Autocomplete,
  Popover,
  Collapse,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  KeyboardArrowDown as DownIcon,
  KeyboardArrowUp as UpIcon,
} from "@mui/icons-material";
import { EditIcon, SaveIcon, GoodIcon } from "./NucleusSymbolsAndIcons";
import {
  useTSQAddress,
  useTSMCreateCity,
  useTSMCreateCountry,
  useTSMCreateState,
} from "../../hooks/useTSAddresses";
import { Controller } from "react-hook-form";
import {
  OrderAddressEdit,
  OrderAddressEditDefaults,
  OrderAddressEditKey,
  AddressFull,
  WorldCityCreate,
  WorldStateCreate,
  WorldCountryCreate,
} from "../../types/addressTypes";

import { TypedSelectOption } from "../../types/common/selectOptions";
import {
  useWorldLocationData,
  WorldLocationContextValue,
} from "../../contexts/WorldLocationContextProvider";
import stringUtils from "../../utils/stringUtils";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import { DynamicRequestFilter } from "../../types/dynamicQueryTypes";
import { TableObject } from "../../types/common/baseTableTypes";
import { IdAsUUID, WorldLocation } from "../../types/common/commonTypes";
import { ViewIcon } from "./NucleusSymbolsAndIcons";

type CountryOption = TypedSelectOption<
  "worldcountry",
  {
    name: string;
    iso2: string;
  },
  number
>;
type StateOption = TypedSelectOption<
  "worldstate",
  {
    name: string;
    country_id: number;
    iso2: string;
  },
  number
>;
type CityOption = TypedSelectOption<
  "worldcity",
  {
    name: string;
    country_id: number;
    state_id: number;
  },
  number
>;

type EntityOption = TypedSelectOption<"entity", {}, string>;
type AddressOption<TValue extends Record<string, any> = {}> = TypedSelectOption<
  "address",
  TValue,
  string
>;
export type AddressSelectorOption = TypedSelectOption<
  "address",
  {
    addressee: string | null;
    attn: string | null;
    line_1: string;
    line_2: string | null;
    line_3: string | null;
    country: WorldLocation<"country">;
    state: WorldLocation<"state">;
    city: WorldLocation<"city">;
    zip: string;
    phone: string | null;
    email: string | null;
  },
  string
>;

interface AddressSelectorErrors {
  main: string | null;
  addressee: string | null;
  attn: string | null;
  line_1: string | null;
  line_2: string | null;
  line_3: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
}

interface AddressSelectorPropsNew<
  TValue extends Record<string, any> = Record<string, any>,
> {
  control: any;
  fieldName: string;
  fieldLabel: string;
  watchedAddr: TableObject<"orderaddress">;
  entityId: IdAsUUID | null;
  preferredAddressId: IdAsUUID | null;
  staticOptions: AddressSelectorOption[];
  onPrefill: (addr: TableObject<"orderaddress">) => void;
  onWorldLocSelect: (type: "country" | "state" | "city", value: string) => void;
  disabled?: boolean;
  ready: boolean;
  errors: AddressSelectorErrors;
}

export const AddressField = <
  TValue extends Record<string, any> = Record<string, any>,
>({
  control,
  fieldName,
  fieldLabel,
  watchedAddr,
  entityId,
  preferredAddressId,
  staticOptions,
  onPrefill,
  onWorldLocSelect,
  disabled,
  ready,
  errors,
}: AddressSelectorPropsNew<TValue>) => {
  const [open, setOpen] = useState<boolean>(false);
  const anchorElRef = useRef<any>(null);
  const initRef = useRef<boolean>(false);

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    null,
  );
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);

  const worldLocContext = useWorldLocationData();

  const [countryOpts, stateOpts, cityOpts] = useMemo(() => {
    let country: CountryOption[] = [];
    let state: StateOption[] = [];
    let city: CityOption[] = [];

    if (worldLocContext) {
      if (worldLocContext.countries.data?.items) {
        country = worldLocContext.countries.data.items.map((c) => ({
          label: c.name,
          type: "worldcountry",
          value: {
            id: c.id,
            name: c.name,
            iso2: c.iso2,
          },
        }));
      }
      if (worldLocContext.states.data?.items) {
        state = worldLocContext.states.data.items.map((c) => ({
          label: c.name,
          type: "worldstate",
          value: {
            id: c.id,
            country_id: c.country_id,
            name: c.name,
            iso2: c.iso2,
          },
        }));
      }
      if (worldLocContext.cities.data?.items) {
        city = worldLocContext.cities.data.items.map((c) => ({
          label: c.name,
          type: "worldcity",
          value: {
            id: c.id,
            name: c.name,
            state_id: c.state_id,
            country_id: c.country_id,
          },
        }));
      }
    }
    return [country, state, city];
  }, [worldLocContext]);

  const filteredStateOpts: StateOption[] = useMemo(() => {
    let opts: StateOption[] = [];
    if (selectedCountry) {
      opts = stateOpts.filter(
        (state) => state.value.country_id === selectedCountry.value.id,
      );
    }
    return opts;
  }, [selectedCountry, stateOpts]);

  const filteredCityOpts: CityOption[] = useMemo(() => {
    let opts: CityOption[] = [];
    if (selectedState) {
      opts = cityOpts.filter(
        (city) => city.value.state_id === selectedState.value.id,
      );
    }
    return opts;
  }, [selectedState, cityOpts]);

  const [selectedAddr, setSelectedAddr] =
    useState<AddressSelectorOption | null>(null);

  const {
    data: addrData,
    isLoading: addrDataLoading,
    isFetching: addrDataFetching,
    error: addrDataError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "addresses",
      alt_name: "addr",
      primary_fields: [],
      joins: [],
      filters: [
        [
          {
            table: "addresses",
            alt_name: "addr",
            field: "entity_id",
            operator: "==",
            value: entityId,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [
        {
          table: "addresses",
          alt_name: "addr",
          field: "line_1",
          asc: true,
        },
      ],
      subqueries: [],
    },
    !disabled && !!entityId && entityId !== "centese",
  );

  const addrOpts: AddressSelectorOption[] = useMemo(() => {
    const addrOpts: AddressSelectorOption[] = [];
    if (addrData?.items) {
      addrData.items.forEach((addr) =>
        addrOpts.push({
          label: addr["addr.name"],
          type: "address",
          value: {
            id: addr["addr.id"],
            addressee: addr["addr.addressee"],
            attn: addr["addr.attn"],
            line_1: addr["addr.line_1"],
            line_2: addr["addr.line_2"],
            line_3: addr["addr.line_3"],
            country: addr["addr.country_obj"],
            state: addr["addr.state_obj"],
            city: addr["addr.city_obj"],
            zip: addr["addr.zip"],
            phone: addr["addr.phone"],
            email: addr["addr.email"],
          },
        }),
      );
    }
    for (let opt of staticOptions) {
      addrOpts.unshift(opt);
    }
    return addrOpts;
  }, [addrData, staticOptions]);

  function handleCountrySelect(value: CountryOption | null) {
    setSelectedCountry(value);
    onWorldLocSelect("country", value?.value.name || "");
    handleStateSelect(null);
  }

  function handleStateSelect(value: StateOption | null) {
    setSelectedState(value);
    onWorldLocSelect("state", value?.value.name || "");
    handleCitySelect(null);
  }

  function handleCitySelect(value: CityOption | null) {
    setSelectedCity(value);
    onWorldLocSelect("city", value?.value.name || "");
  }

  function handleOpen() {
    if (open) {
      setOpen(false);
    } else {
      if (watchedAddr.line_1 !== "") {
        const countryOpt =
          countryOpts.find(
            (opt) =>
              opt.value.name === watchedAddr.country ||
              opt.value.iso2 === watchedAddr.country,
          ) || null;
        setSelectedCountry(countryOpt);
        const stateOpt =
          stateOpts.find((opt) => {
            if (countryOpt) {
              return (
                opt.value.country_id === countryOpt.value.id &&
                (opt.value.name === watchedAddr.state ||
                  opt.value.iso2 === watchedAddr.state)
              );
            } else {
              return (
                opt.value.name === watchedAddr.state ||
                opt.value.iso2 === watchedAddr.state
              );
            }
          }) || null;
        setSelectedState(stateOpt);
        const cityOpt =
          cityOpts.find(
            (opt) =>
              (opt.value.country_id === countryOpt?.value.id || true) &&
              (opt.value.state_id === stateOpt?.value.id || true) &&
              opt.value.name === watchedAddr.city,
          ) || null;
        setSelectedCity(cityOpt);
      } else {
        if (addrOpts.length > 0) {
          handleAddrChange(addrOpts[0]);
        }
      }

      setOpen(true);
    }
  }

  function handleAddrChange(newValue: AddressSelectorOption | null) {
    setSelectedAddr(newValue);
    onPrefill({
      attn: newValue?.value.attn || null,
      addressee: newValue?.value.addressee || null,
      line_1: newValue?.value.line_1 || "",
      line_2: newValue?.value.line_2 || null,
      line_3: newValue?.value.line_3 || null,
      city: newValue?.value.city.name || "",
      state: newValue?.value.state.name || "",
      country: newValue?.value.country.name || "",
      zip: newValue?.value.zip || "",
      phone: newValue?.value.phone || null,
      email: newValue?.value.email || null,
    });
    if (newValue) {
      setSelectedCountry(
        countryOpts.find(
          (opt) => opt.value.name === newValue.value.country.name,
        ) || null,
      );
      setSelectedState(
        stateOpts.find((opt) => opt.value.name === newValue.value.state.name) ||
          null,
      );
      setSelectedCity(
        cityOpts.find((opt) => opt.value.name === newValue.value.city.name) ||
          null,
      );
    } else {
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
    }
  }

  useEffect(() => {
    if (ready) {
      if (!initRef.current) {
        initRef.current = true;
        return;
      }
      console.log("ADDR DATA PREF ID USEEFFECT", {
        fetching: addrDataFetching,
        prefId: preferredAddressId,
        opts: addrOpts,
      });
      if (!addrDataFetching && initRef.current) {
        if (!!preferredAddressId) {
          const prefOpt =
            addrOpts.find((opt) => opt.value.id === preferredAddressId) || null;
          handleAddrChange(prefOpt);
        } else {
          handleAddrChange(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrDataFetching, ready, preferredAddressId]);

  return (
    <Stack direction={"column"} width={"100%"}>
      <TextField
        value={
          watchedAddr.line_1 === ""
            ? ""
            : `${watchedAddr.line_1}\n${watchedAddr.city}, ${watchedAddr.state}\n${watchedAddr.country} ${watchedAddr.zip}`
        }
        disabled={disabled}
        onChange={() => {}}
        size="small"
        multiline
        rows={3}
        placeholder="Press the pencil 'Edit' button => to select or create an address."
        label={fieldLabel}
        error={!!errors["main"]}
        helperText={errors["main"]}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    disabled
                      ? "View Full Address"
                      : open
                        ? "Close Edit Panel"
                        : "Edit Address"
                  }
                >
                  <IconButton onClick={handleOpen} edge="end" size="small">
                    {disabled ? (
                      <ViewIcon />
                    ) : open ? (
                      <GoodIcon />
                    ) : (
                      <EditIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
            readOnly: true,
          },
        }}
      />
      <Collapse in={open} timeout="auto" sx={{ width: "100%" }} unmountOnExit>
        <Stack
          direction="column"
          spacing={1}
          width="100%"
          p={2}
          sx={{
            border: "1px solid black",
            borderRadius: 2,
          }}
        >
          {!disabled && (
            <Autocomplete
              value={selectedAddr}
              options={addrOpts}
              fullWidth
              ref={anchorElRef}
              onChange={(event, newValue) => {
                console.log("Addr Autocomplete onChange", newValue);
                handleAddrChange(newValue);
              }}
              loading={addrDataFetching}
              loadingText={"Loading Vendor Addresses..."}
              disabled={disabled}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    label={"Prefill Selector"}
                    size="small"
                  />
                );
              }}
            />
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gridAutoRows: "auto",
              gap: 1,
              width: "100%",
            }}
          >
            <Controller
              control={control}
              name={`${fieldName}.addressee`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 1,
                    gridRow: 1,
                  }}
                  {...restField}
                  label="Addressee"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["addressee"]}
                  helperText={errors["addressee"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.attn`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                  }}
                  {...restField}
                  label="Attention"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["attn"]}
                  helperText={errors["attn"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.line_1`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumnStart: 1,
                    gridColumnEnd: 3,
                    gridRow: 2,
                  }}
                  {...restField}
                  label="Line 1"
                  value={value}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["line_1"]}
                  helperText={errors["line_1"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.line_2`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 1,
                    gridRow: 3,
                  }}
                  {...restField}
                  label="Line 2"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["line_2"]}
                  helperText={errors["line_2"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.line_3`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 2,
                    gridRow: 3,
                  }}
                  {...restField}
                  label="Line 3"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["line_3"]}
                  helperText={errors["line_3"]}
                />
              )}
            />
            <Autocomplete
              sx={{
                gridColumn: 1,
                gridRow: 4,
              }}
              fullWidth
              value={selectedCountry}
              options={countryOpts}
              onChange={(e, v) => {
                handleCountrySelect(v);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Country"
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  error={!!errors["country"]}
                  helperText={errors["country"]}
                />
              )}
            />
            <Autocomplete
              sx={{
                gridColumn: 2,
                gridRow: 4,
              }}
              fullWidth
              value={selectedState}
              options={filteredStateOpts}
              onChange={(e, v) => {
                handleStateSelect(v);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              disabled={!selectedCountry || disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  error={!!errors["state"]}
                  helperText={errors["state"]}
                />
              )}
            />
            <Autocomplete
              sx={{
                gridColumn: 1,
                gridRow: 5,
              }}
              fullWidth
              value={selectedCity}
              options={filteredCityOpts}
              onChange={(e, v) => {
                handleCitySelect(v);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value.value.id
              }
              disabled={!selectedState || disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  error={!!errors["city"]}
                  helperText={errors["city"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.zip`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 2,
                    gridRow: 5,
                  }}
                  {...restField}
                  label="Zip"
                  value={value}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["zip"]}
                  helperText={errors["zip"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.phone`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 1,
                    gridRow: 6,
                  }}
                  {...restField}
                  label="Phone"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["phone"]}
                  helperText={errors["phone"]}
                />
              )}
            />
            <Controller
              control={control}
              name={`${fieldName}.email`}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { isDirty },
              }) => (
                <TextField
                  sx={{
                    gridColumn: 2,
                    gridRow: 6,
                  }}
                  {...restField}
                  label="Email"
                  value={value || ""}
                  onChange={onChange}
                  fullWidth
                  size="small"
                  autoComplete="off"
                  aria-autocomplete="none"
                  disabled={disabled}
                  error={!!errors["email"]}
                  helperText={errors["email"]}
                />
              )}
            />
          </Box>
        </Stack>
      </Collapse>
    </Stack>
  );
};

interface AddressSelectorProps<
  TValue extends Record<string, any> = Record<string, any>,
> {
  control: any;
  fieldName: string;
  selectLabel: string;
  formMode: string;
  watchedAddrOpt: AddressOption<TValue> | null;
  availAddr: AddressOption<TValue>[];
  existingEdit: OrderAddressEdit | null;
  setNewEdits: React.Dispatch<React.SetStateAction<OrderAddressEdit>>;
  worldLocContext: WorldLocationContextValue | undefined;
  hasError: boolean;
  errorText?: string;
}

export const AddressFieldOld = <
  TValue extends Record<string, any> = Record<string, any>,
>({
  control,
  fieldName,
  selectLabel,
  formMode,
  watchedAddrOpt,
  availAddr,
  existingEdit,
  setNewEdits,
  worldLocContext,
  hasError,
  errorText,
}: AddressSelectorProps<TValue>) => {
  const [open, setOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const anchorElRef = useRef<any>(null);
  const [thisAddrEdit, setThisAddrEdit] = useState<OrderAddressEdit>(
    OrderAddressEditDefaults,
  );

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    null,
  );
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);

  const [countryOpts, stateOpts, cityOpts] = useMemo(() => {
    let country: CountryOption[] = [];
    let state: StateOption[] = [];
    let city: CityOption[] = [];

    if (worldLocContext) {
      if (worldLocContext.countries.data?.items) {
        country = worldLocContext.countries.data.items.map((c) => ({
          label: c.name,
          type: "worldcountry",
          value: {
            id: c.id,
            name: c.name,
            iso2: c.iso2,
          },
        }));
      }
      if (worldLocContext.states.data?.items) {
        state = worldLocContext.states.data.items.map((c) => ({
          label: c.name,
          type: "worldstate",
          value: {
            id: c.id,
            country_id: c.country_id,
            name: c.name,
            iso2: c.iso2,
          },
        }));
      }
      if (worldLocContext.cities.data?.items) {
        city = worldLocContext.cities.data.items.map((c) => ({
          label: c.name,
          type: "worldcity",
          value: {
            id: c.id,
            name: c.name,
            state_id: c.state_id,
            country_id: c.country_id,
          },
        }));
      }
    }
    return [country, state, city];
  }, [worldLocContext]);

  const filteredStateOpts: StateOption[] = useMemo(() => {
    let opts: StateOption[] = [];
    if (selectedCountry) {
      opts = stateOpts.filter(
        (state) => state.value.country_id === selectedCountry.value.id,
      );
    }
    return opts;
  }, [selectedCountry, stateOpts]);

  const filteredCityOpts: CityOption[] = useMemo(() => {
    let opts: CityOption[] = [];
    if (selectedState) {
      opts = cityOpts.filter(
        (city) => city.value.state_id === selectedState.value.id,
      );
    }
    return opts;
  }, [selectedState, cityOpts]);

  const {
    data: baseAddrData,
    isLoading: baseAddrLoading,
    error: baseAddrError,
  } = useTSQAddress(watchedAddrOpt?.value.id || "", !!watchedAddrOpt);

  const handleAddrEdit = (field: OrderAddressEditKey) => (event: any) => {
    let newVal: string | null = null;
    if (event.target.value && event.target.value !== "") {
      newVal = event.target.value;
    }
    setThisAddrEdit((prev) => ({
      ...prev,
      [field]: newVal,
    }));
  };

  const handleCountrySelect = (value: CountryOption | null) => {
    setSelectedCountry(value);
    setThisAddrEdit((prev) => ({
      ...prev,
      country: value?.value.name || null,
    }));
    handleStateSelect(null);
  };

  const handleStateSelect = (value: StateOption | null) => {
    setSelectedState(value);
    setThisAddrEdit((prev) => ({
      ...prev,
      state: value?.value.name || null,
    }));
    handleCitySelect(null);
  };

  const handleCitySelect = (value: CityOption | null) => {
    setSelectedCity(value);
    setThisAddrEdit((prev) => ({
      ...prev,
      city: value?.value.name || null,
    }));
  };

  const handleOpen = () => {
    if (existingEdit) {
      setThisAddrEdit(existingEdit);
      let countryObj =
        countryOpts.find(
          (c) =>
            c.value.name === existingEdit.country ||
            c.value.iso2 === existingEdit.country,
        ) || null;
      setSelectedCountry(countryObj);
      let stateObj =
        stateOpts.find(
          (s) =>
            (s.value.name === existingEdit.state ||
              s.value.iso2 === existingEdit.state) &&
            s.value.country_id === countryObj?.value.id,
        ) || null;
      setSelectedState(stateObj);
      let cityObj =
        cityOpts.find(
          (c) =>
            c.value.name === existingEdit.city &&
            c.value.state_id === stateObj?.value.id &&
            c.value.country_id === countryObj?.value.id,
        ) || null;
      setSelectedCity(cityObj);
    } else {
      setThisAddrEdit(OrderAddressEditDefaults);
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    handleAddrSave();
  };

  // The saved address's value for an editable field, or null if it has none.
  const baseValue = (key: OrderAddressEditKey): string | null => {
    const v = baseAddrData ? baseAddrData[key] : undefined;
    return typeof v === "string" && v !== "" ? v : null;
  };

  const handleEditButtonPress = (event: any) => {
    if (editMode) {
      handleAddrSave();
      return;
    }
    // Start from the address as it currently reads (saved address plus any
    // existing edits) so only the part that needs changing has to be retyped
    // (J. Whybark). Fields the user leaves equal to the saved address are
    // dropped again on save, so this does not turn every field into an edit.
    if (baseAddrData) {
      setThisAddrEdit((prev) => {
        const next: OrderAddressEdit = { ...prev };
        (Object.keys(next) as OrderAddressEditKey[]).forEach((key) => {
          if (!next[key]) next[key] = baseValue(key);
        });
        return next;
      });
      if (!selectedCountry) {
        setSelectedCountry(
          countryOpts.find(
            (c) => c.value.id === baseAddrData.country_obj?.id,
          ) || null,
        );
      }
      if (!selectedState) {
        setSelectedState(
          stateOpts.find((s) => s.value.id === baseAddrData.state_obj?.id) ||
            null,
        );
      }
      if (!selectedCity) {
        setSelectedCity(
          cityOpts.find((c) => c.value.id === baseAddrData.city_obj?.id) ||
            null,
        );
      }
    }
    setEditMode(true);
  };

  const handleAddrSave = () => {
    if (formMode === "create" || formMode === "edit") {
      // Keep only genuine overrides: a field that still matches the saved
      // address is not an edit.
      const cleaned: OrderAddressEdit = { ...thisAddrEdit };
      (Object.keys(cleaned) as OrderAddressEditKey[]).forEach((key) => {
        const value = cleaned[key];
        const base = baseValue(key);
        if (
          value === null ||
          value.trim() === "" ||
          (base !== null && value.trim() === base.trim())
        ) {
          cleaned[key] = null;
        }
      });
      setThisAddrEdit(cleaned);
      setNewEdits(cleaned);
      setEditMode(false);
    }
    setOpen(false);
  };
  return (
    <Stack sx={{ direction: "column", gap: 1, width: "100%" }}>
      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
        <Controller
          name={fieldName}
          control={control}
          render={({ field: { onChange, value, ...restField } }) => {
            return (
              <Autocomplete
                {...restField}
                value={value}
                fullWidth
                ref={anchorElRef}
                onChange={(event, newValue) => {
                  console.log(`${selectLabel} selection change!`, newValue);
                  onChange(newValue);
                }}
                options={availAddr}
                disabled={!["create", "edit"].includes(formMode)}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.value === value?.value
                }
                renderInput={(params) => {
                  const endAdorn: React.ReactNode = (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <InputAdornment position="end">
                        <Tooltip title="Edit Address">
                          <IconButton
                            onClick={handleOpen}
                            edge="end"
                            size="small"
                            disabled={
                              formMode === "view" || formMode === "receive"
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </Box>
                  );
                  params.InputProps.endAdornment = endAdorn;
                  return (
                    <TextField
                      {...params}
                      label={selectLabel}
                      error={hasError}
                      helperText={errorText}
                    />
                  );
                }}
              />
            );
          }}
        />
        <Popover
          open={open}
          anchorEl={anchorElRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          slotProps={{
            paper: {
              sx: {
                width: anchorElRef.current
                  ? `${anchorElRef.current.offsetWidth * 1.5}px`
                  : "auto",
              },
            },
          }}
        >
          {baseAddrData ? (
            <Stack
              sx={{
                direction: "column",
                gap: 1,
                p: 1,
                width: "100%",
              }}
            >
              {(formMode === "create" || formMode === "edit") && (
                <Button
                  variant="outlined"
                  color={editMode ? "primary" : "info"}
                  onClick={handleEditButtonPress}
                >
                  {editMode ? "Save Edits" : "Edit Address"}
                </Button>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gridAutoRows: "auto",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 1 }}
                >
                  Addressee:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 1,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.addressee
                        ? thisAddrEdit.addressee
                        : baseAddrData.addressee || "-"}
                    </Typography>
                    {thisAddrEdit.addressee && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 2 }}
                >
                  Attn:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.attn
                        ? thisAddrEdit.attn
                        : baseAddrData.attn || "-"}
                    </Typography>
                    {thisAddrEdit.attn && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 3 }}
                >
                  Line 1:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 3,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.line_1
                        ? thisAddrEdit.line_1
                        : baseAddrData.line_1}
                    </Typography>
                    {thisAddrEdit.line_1 && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 4 }}
                >
                  Line 2:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 4,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.line_2
                        ? thisAddrEdit.line_2
                        : baseAddrData.line_2 || "-"}
                    </Typography>
                    {thisAddrEdit.line_2 && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 1, gridRow: 5 }}
                >
                  Line 3:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 5,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.line_3
                        ? thisAddrEdit.line_3
                        : baseAddrData.line_3 || "-"}
                    </Typography>
                    {thisAddrEdit.line_3 && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 1 }}
                >
                  City:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 1,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.city
                        ? thisAddrEdit.city
                        : baseAddrData.city_obj.name}
                    </Typography>
                    {thisAddrEdit.city && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 2 }}
                >
                  State:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 2,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.state
                        ? thisAddrEdit.state
                        : baseAddrData.state_obj.name}
                    </Typography>
                    {thisAddrEdit.state && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 3 }}
                >
                  Country:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 3,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.country
                        ? thisAddrEdit.country
                        : baseAddrData.country_obj.name}
                    </Typography>
                    {thisAddrEdit.country && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 4 }}
                >
                  Zip:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 4,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.zip ? thisAddrEdit.zip : baseAddrData.zip}
                    </Typography>
                    {thisAddrEdit.zip && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 5 }}
                >
                  Phone:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 5,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.phone
                        ? thisAddrEdit.phone
                        : baseAddrData.phone || "-"}
                    </Typography>
                    {thisAddrEdit.phone && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ gridColumn: 3, gridRow: 6 }}
                >
                  Email:
                </Typography>
                <Box
                  sx={{
                    gridColumn: 4,
                    gridRow: 6,
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Typography variant="subtitle1">
                      {thisAddrEdit.email
                        ? thisAddrEdit.email
                        : baseAddrData.email || "-"}
                    </Typography>
                    {thisAddrEdit.email && (
                      <Typography variant="body2" color="red">
                        *
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {editMode && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gridAutoRows: "auto",
                    gap: 1,
                  }}
                >
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 1,
                    }}
                    label="Addressee Edit"
                    value={thisAddrEdit.addressee}
                    onChange={handleAddrEdit("addressee")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 2,
                    }}
                    label="Attn Edit"
                    value={thisAddrEdit.attn}
                    onChange={handleAddrEdit("attn")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 3,
                    }}
                    label="Line 1 Edit"
                    value={thisAddrEdit.line_1}
                    onChange={handleAddrEdit("line_1")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 4,
                    }}
                    label="Line 2 Edit"
                    value={thisAddrEdit.line_2}
                    onChange={handleAddrEdit("line_2")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 1,
                      gridRow: 5,
                    }}
                    label="Line 3 Edit"
                    value={thisAddrEdit.line_3}
                    onChange={handleAddrEdit("line_3")}
                  />
                  <Autocomplete
                    sx={{
                      gridColumn: 2,
                      gridRow: 1,
                    }}
                    fullWidth
                    value={selectedCountry}
                    options={countryOpts}
                    onChange={(e, v) => {
                      handleCountrySelect(v);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value.value.id
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Country Edit" />
                    )}
                  />
                  {/* <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 1,
                    }}
                    label="Country Edit"
                    value={thisAddrEdit.country}
                    onChange={handleAddrEdit("country")}
                  /> */}
                  <Autocomplete
                    sx={{
                      gridColumn: 2,
                      gridRow: 2,
                    }}
                    fullWidth
                    value={selectedState}
                    options={filteredStateOpts}
                    onChange={(e, v) => {
                      handleStateSelect(v);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value.value.id
                    }
                    disabled={!selectedCountry}
                    renderInput={(params) => (
                      <TextField {...params} label="State Edit" />
                    )}
                  />
                  {/* <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 2,
                    }}
                    label="State Edit"
                    value={thisAddrEdit.state}
                    onChange={handleAddrEdit("state")}
                  /> */}
                  <Autocomplete
                    sx={{
                      gridColumn: 2,
                      gridRow: 3,
                    }}
                    fullWidth
                    value={selectedCity}
                    options={filteredCityOpts}
                    onChange={(e, v) => {
                      handleCitySelect(v);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value.id === value.value.id
                    }
                    disabled={!selectedState}
                    renderInput={(params) => (
                      <TextField {...params} label="City Edit" />
                    )}
                  />
                  {/* <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 3,
                    }}
                    label="City Edit"
                    value={thisAddrEdit.city}
                    onChange={handleAddrEdit("city")}
                  /> */}
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 4,
                    }}
                    label="Zip Edit"
                    value={thisAddrEdit.zip}
                    onChange={handleAddrEdit("zip")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 5,
                    }}
                    label="Phone Edit"
                    value={thisAddrEdit.phone}
                    onChange={handleAddrEdit("phone")}
                  />
                  <TextField
                    sx={{
                      gridColumn: 2,
                      gridRow: 6,
                    }}
                    label="Email Edit"
                    value={thisAddrEdit.email}
                    onChange={handleAddrEdit("email")}
                  />
                </Box>
              )}
            </Stack>
          ) : (
            <Typography variant="h6">Missing Base Address Data</Typography>
          )}
        </Popover>
      </Box>
    </Stack>
  );
};
